<template>
  <v-app>
    <div id="app-notifications">
      <app-notification
        width="480"
        class="mb-2"
        :max="5"
        position="bottom center"
        :duration="5000" />
    </div>
    <v-main>
      <v-banner
        class="d-flex flex-column site-banner is-relative"
        v-if="!$route.meta.shouldHideBanner">
        <v-menu>
          <template v-slot:activator="{ props }">
            <v-btn size="small" id="template-generator-button" color="primary" v-bind="props">
              Generate Template
              <v-tooltip location="bottom" activator="parent">
                Generate an Excel Import Template
              </v-tooltip>
            </v-btn>
          </template>
          <v-list>
            <v-list-item v-for="(item, index) in IMPORT_TEMPLATES" :key="index" :value="index">
              <v-btn
                size="small"
                class="text-left justify-start"
                plain
                block
                @click="generateImportTemplate(item.type)">
                {{ item.label }}
              </v-btn>
            </v-list-item>
          </v-list>
        </v-menu>
        <h1 class="text-center">Welcome to Photon!</h1>
        <p class="text-center pt-2">Data export/import service for Nova</p>
        <v-btn v-if="store.state.me" variant="text" @click="logout" id="logout-btn">Logout</v-btn>
      </v-banner>
      <v-container
        :fluid="$route.meta.isFullScreen"
        :class="{ 'ma-0 pa-0': $route.meta.isFullScreen }">
        <router-view></router-view>
      </v-container>
    </v-main>
  </v-app>
</template>

<script lang="ts" setup>
import AppNotification from '@/components/AppNotification.vue';
import { useStore } from 'vuex';
import { successRes } from '@/services/util';
import { useRouting } from '@/composables/router';
import { exportWorkbook, IExportedData } from '@/services/workbookService';
import { useRoute } from 'vue-router';
import { NovaUserRoles } from '@/enums/nova/novaEnums';

const store = useStore();
const { routeLoggedIn, routeLoggedOut } = useRouting();
const route = useRoute();

enum templateTypes {
  USERS = 'users',
  CARRIERS = 'carriers',
  ALLUSERS = 'users-carriers'
}

const IMPORT_TEMPLATES = {
  [templateTypes.USERS]: {
    label: 'Users',
    type: templateTypes.USERS
  },
  [templateTypes.CARRIERS]: {
    label: 'Carriers',
    type: templateTypes.CARRIERS
  },
  [templateTypes.ALLUSERS]: {
    label: 'Users and Carriers',
    type: templateTypes.ALLUSERS
  }
};

const handleInitialToken = async () => {
  if (route.query?.token) {
    await store.dispatch('login', route.query.token);
  }

  await store.dispatch('setInitialAccessToken');

  try {
    const response = await store.dispatch('getMe');
    if (successRes(response.status) && response.data?.role === NovaUserRoles.INTERNAL) {
      await routeLoggedIn();
    } else {
      await routeLoggedOut();
    }
  } catch (e) {
    await routeLoggedOut();
  }
};

const logout = async () => {
  await store.dispatch('logout');
  await routeLoggedOut();
};

handleInitialToken();

const generateImportTemplate = (type: templateTypes) => {
  switch (type) {
    case templateTypes.USERS: {
      exportWorkbook(
        buildUsersTemplate() as Partial<IExportedData>,
        `${IMPORT_TEMPLATES[templateTypes.USERS].label} Import Template`,
        ''
      );
      break;
    }
    case templateTypes.CARRIERS: {
      exportWorkbook(
        buildCarriersTemplate() as Partial<IExportedData>,
        `${IMPORT_TEMPLATES[templateTypes.CARRIERS].label} Import Template`,
        ''
      );
      break;
    }
    case templateTypes.ALLUSERS: {
      exportWorkbook(
        { ...buildUsersTemplate(), ...buildCarriersTemplate() } as Partial<IExportedData>,
        `${IMPORT_TEMPLATES[templateTypes.ALLUSERS].label} Import Template`,
        ''
      );
      break;
    }
    default: {
      break;
    }
  }
};

function buildUsersTemplate() {
  return {
    users: [
      {
        firstName: 'User',
        lastName: 'Name',
        email: 'useremail@test.com',
        role: 'role_admin',
        warehouseAccessList: 'Warehouse Name 1; Warehouse Name 2'
      }
    ]
  };
}

function buildCarriersTemplate() {
  return {
    carriers: [
      {
        firstName: 'Carrier',
        lastName: 'Name',
        email: 'carrieremail@test.com',
        phone: '5555555555',
        company: 'Company Name'
      }
    ]
  };
}
</script>

<style lang="scss">
html {
  font-size: 16px;
}

body {
  font-size: 1rem;
  overflow-x: hidden;
}

h1,
h2,
h3,
h4,
h5,
h6,
[class*='text-h'],
button {
  font-family: $headerButtonFont;
  font-weight: 600 !important;
}

.tile {
  padding: 0.25rem 0.25rem 0.4rem;
  border-radius: 5px;
  color: $white;
}

.text-lg-h6 {
  text-transform: capitalize !important;
  font-weight: 400 !important;
}

.opacity-60 {
  opacity: 0.6;
}

.v-field--active {
  overflow: visible !important;
}

.text {
  &--error {
    color: $error;
  }
}

button {
  &.error {
    background: $error;
  }

  &.black--text {
    color: $black !important;
  }
}

#logout-btn {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
}

#template-generator-button {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
}

.v-overlay {
  width: 100% !important;
}

.v-overlay__content {
  max-width: 100%;
}

.dialog-box {
  max-width: 100%;
}

.v-card-title {
  &.error {
    background: $error;
    color: $white;
  }
}

.bordered {
  border: 1px solid currentColor;
}

#app.app-appointments {
  .v-main {
    &__wrap {
      > .container {
        height: 100vh;
      }
    }
  }
}

.non-active-tab-underline .v-tab:not(.v-tab--active) {
  position: relative;
  color: $midGrey;

  &:after {
    content: ' ';
    height: 2px;
    background: $midGrey;
    left: 0;
    right: 0;
    position: absolute;
    bottom: 0;
  }
}

.v-application {
  [class*='text-']:not([class*='text-h']),
  p {
    font-family: $bodyFont;
  }

  font-family: $bodyFont;

  .required {
    label {
      &:after {
        content: '*';
        color: $error;
        margin-left: 0.5rem;
      }
    }
  }

  .shake-element {
    animation: shake 1s;
  }

  .site-banner {
    background: $secondary;
    color: $white;
  }

  @keyframes shake {
    10%,
    90% {
      transform: translate3d(-1px, 0, 0);
    }

    20%,
    80% {
      transform: translate3d(2px, 0, 0);
    }

    30%,
    50%,
    70% {
      transform: translate3d(-4px, 0, 0);
    }

    40%,
    60% {
      transform: translate3d(4px, 0, 0);
    }
  }

  .viewport-height {
    height: 100vh;
  }

  .v-main {
    &__wrap {
      > .container {
        display: flex;

        .app-container-wrapper {
          flex: 1;
          height: 100%;
          display: flex;
          flex-direction: column;
          min-height: 0;
        }
      }
    }
  }

  .v-data-table {
    &:not(.select) {
      .v-data-table__selected {
        background-color: #f24848;
      }
    }

    &.select {
      td {
        &:hover {
          cursor: pointer;
        }
      }
    }

    .v-data-table__selected {
      background-color: #02809f;
      color: white;
    }

    &.clickable-rows tbody tr :hover {
      cursor: pointer;
    }
  }

  .handle {
    cursor: move;
  }

  .v-dialog {
    .v-card {
      max-height: 750px;
      overflow: auto;

      &__title {
        position: sticky;
        top: 0;
        background-color: white;
        z-index: 1;
      }

      &__actions {
        position: sticky;
        bottom: -1.5rem;
        background-color: white;
        z-index: 1;
      }
    }
  }

  [role='combobox'] {
    [del='search'] {
      overflow: hidden;
      text-overflow: ellipsis;
      min-width: 6rem;
    }
  }

  .v-text-field__slot,
  .v-text-field__ {
    margin-top: -5px !important;
  }

  .label-with-tooltip {
    .v-label {
      pointer-events: all !important;
    }

    .v-select__selections {
      z-index: 1;
    }
  }

  .z-2 {
    position: relative;
    z-index: 2;
  }

  .v-select {
    min-width: 6rem;

    &__selections {
      flex-wrap: nowrap;

      & > span:first-child {
        white-space: nowrap;
        max-width: 80%;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      & > span:nth-child(2) {
        min-width: 70px;
      }
    }
  }

  form {
    .row {
      h3 {
        margin-top: 1rem;
        white-space: nowrap;
        margin-right: 1rem;
      }
    }

    padding-top: 0.75rem !important;

    .col {
      padding-top: 0;
      padding-bottom: 0;
    }

    .v-input {
      padding-top: 0 !important;

      &--dense {
        .v-input__icon,
        .v-input__icon--prepend {
          height: 12px;
        }
      }
    }

    .v-input {
      margin-top: 0.9rem;

      &:not(.v-autocomplete):not(.v-select):not([class*='mt-']) {
        //margin-top: 1.25rem !important;
      }

      &__details {
        margin-bottom: 0;
      }

      .v-field__field {
        min-height: 40px;
      }
    }
  }

  .full-height {
    height: 100%;
  }

  .full-width {
    width: 100%;
  }

  .height-auto {
    height: auto !important;
  }

  .no-underline {
    text-decoration: none;
  }

  .cursorText {
    font-family: $bodyFont;
    position: fixed;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 5px;
    padding: 10px;
    background-color: #fff;
    font-size: 10px;
  }

  .field-label-tooltip {
    vertical-align: top !important;
    cursor: help;
  }

  .vertical-center {
    vertical-align: middle !important;
  }

  footer.version {
    height: 100px;
    display: flex !important;
    align-items: flex-end;
    line-height: 1.2rem !important;
    font-size: 0.65rem !important;
    z-index: 1;
    background-color: var(--v-secondary-base) !important;
  }

  .status-stepper {
    .v-stepper__items {
      background-color: $disabled !important;
    }

    .card-content {
      background-color: transparent !important;
      border: none !important;
    }
  }

  .is-relative {
    position: relative;
  }

  .text {
    &__error {
      color: $error !important;

      * {
        font-weight: bold;
      }
    }

    &--error {
      color: $error !important;
    }
  }

  .has-dialog-actions {
    .v-card__text {
      padding: 0 !important;

      form {
        > *:not(.v-card__actions) {
          padding: 0 1rem;
        }
      }
    }
  }

  .v-list-item.input-instructions {
    min-height: auto;

    .v-list-item__title {
      font-size: 12px;
    }

    .v-list-item__content {
      padding: 0;
    }
  }
}

.font-size-large {
  &.input {
    input {
      font-size: 1.5rem;
    }
  }
}

.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.8s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(20px);
  opacity: 0;
}

.font-size-small {
  font-size: 14px !important;
}

.font-size-x-small {
  font-size: 12px !important;
  line-height: 12px !important;

  i {
    font-size: 12px !important;
  }

  &.v-btn--icon.v-size--default {
    height: 15px !important;
    width: 15px !important;
  }
}

.is-visibility-hidden {
  visibility: hidden;
}

.v-list-item,
.v-list-group {
  > div {
    transition: opacity 0.4s ease;
  }

  &.disabled-list-item {
    > div {
      color: $white !important;
      opacity: 0.2;
    }
  }
}

.cancelled {
  opacity: 0.6;

  .event-container {
    color: $black;
  }
}

.overlay {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  opacity: 0.6;
  background-color: rgba(0, 0, 0, 0.5);
}

.img-cover {
  background-size: cover;
  background-position: center;
}

.sub-title {
  font-weight: bold;
  font-size: 0.8rem;
  color: rgba(0, 0, 0, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.075rem;
  text-align: center;
  display: block;
}

.text {
  &--orange {
    color: orange;
  }
}

.underlined {
  text-decoration: underline !important;
}

.primary {
  color: $black !important;
}

a {
  &:not(.v-list-item--link):not([class*='v-tab']) {
    text-decoration: none;
    font-weight: bold;
    color: $secondary !important;

    &.primary-button {
      background-color: var(--v-primary-base);
      color: $black !important;
      font-weight: 400;
      border-radius: 5px;
    }

    &.download-button {
      border: 1px solid $black;
      padding: 0.25rem 1rem;
      color: $black !important;
      border-radius: 5px;
    }
  }
}

.header-bar-logo {
  width: 125px;
}

#duration-toolbar .v-toolbar__content {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.cursor-pointer {
  cursor: pointer;
}

.mobile-header {
  display: none;
  background-color: var(--v-secondary-base);
  max-height: 0;
  transition: max-height 0.4s ease;

  .mobile-logo {
    width: 125px;
    height: auto;
  }

  @media (max-width: 1023px) {
    max-height: 85px;
    display: flex;
    align-items: center;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 7;
  }
}

header.time-select-header {
  .legend-block {
    display: inline-block;
    width: 50px;
    height: 20px;

    &.is-border-all {
      border: 1px solid black;
    }
  }
}

.header-text {
  font-size: 1rem !important;
  font-family: 'Barlow', sans-serif !important;
}

.welcome-image {
  width: 300px;
  max-width: 100%;
}

@media (max-width: 1023px) {
  .v-application:not(.hide-sidebar) {
    .v-application--wrap {
      padding-top: 4rem;
    }
  }
}

.nova-logo {
  position: sticky;
  top: 0;
  background-color: var(--v-secondary-base);
  z-index: 1;
}

.flex-break {
  flex-basis: 100%;
  height: 0;
}

.row-actions {
  white-space: nowrap;
  display: flex;
  justify-content: flex-end;
}

#schedule-table {
  th {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
}

.page-wrapper {
  overflow: auto;
  height: 100%;
}

.is-uppercase {
  text-transform: uppercase;
}

@media (max-width: 600px) {
  .v-data-table__mobile-table-row {
    padding: 1rem 0;
    border-bottom: thin solid rgba(0, 0, 0, 0.12);
  }
  .theme--light.v-data-table
    > .v-data-table__wrapper
    > table
    > tbody
    > tr:not(:last-child)
    > td.v-data-table__mobile-row:last-child {
    border-bottom: none !important;
  }
  .v-data-table > .v-data-table__wrapper .v-data-table__mobile-row {
    min-height: 25px !important;
  }
  .row {
    width: calc(100% + 1rem);
  }
  form button {
    width: 100%;
  }
  .sortable {
    &.v-chip {
      background: transparent !important;
      border-radius: 0;
      padding: 0;
    }
  }
  .search-field {
    display: flex;
    order: 2;
    margin-top: 1rem !important;
  }
  .action-button {
    display: flex;
    order: 1;
  }

  .v-card__actions {
    flex-wrap: wrap;

    button:not(:first-of-type) {
      margin-top: 1rem;
    }
  }

  .v-application--is-ltr .v-card__actions > .v-btn.v-btn + .v-btn {
    margin-left: 0 !important;
  }

  .v-data-table__mobile-row__header {
    flex-grow: 1;
  }

  .v-data-table {
    .v-chip.v-chip--label.theme--light.v-size--small.primary span:last-child {
      display: block;
      padding-top: 0.1rem;
    }

    .v-chip.v-chip--label.theme--light.v-size--small.primary {
      max-width: 35vw;
    }
  }

  header.time-select-header {
    height: auto !important;

    .v-toolbar__content {
      margin: 1rem 0;
      height: auto !important;
      justify-content: center;

      .spacer {
        display: none;
      }

      button:not(:first-of-type) {
        margin-top: 1rem;
      }
    }
  }
}

#app-notifications {
  .vue-notification-wrapper {
    &:not(:first-child) {
      margin-top: 0.5rem;
    }
  }

  .app-notification {
    border-radius: 5px;
    position: relative;
    padding: 0.5rem 1rem;

    i.type-icon {
      color: $white;
    }

    &.success {
      background-color: $white !important;
      border: 1px solid $success !important;
      border-left: 40px solid $success !important;
      color: $black;
    }

    &.error {
      background-color: $white !important;
      border: 1px solid $error !important;
      border-left: 40px solid $error !important;
      color: $black;
    }

    &.warning {
      background-color: $white !important;
      border: 1px solid $warning !important;
      border-left: 40px solid $warning !important;
      color: $black;
    }

    &.info {
      background-color: $white !important;
      border: 1px solid $info !important;
      border-left: 40px solid $info !important;
      color: $black;
    }

    .title {
      font-size: 1rem !important;
      margin-bottom: 0;
      line-height: 1.25rem;
    }

    i.type-icon {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      left: 0;
      margin-left: -35px;
      font-size: 2rem;
      z-index: 15;
    }

    .close {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      padding: 0 0.5rem;

      i {
        font-size: 12px;
      }
    }
  }
}

.disable-label-click {
  .v-input__slot,
  label {
    &:hover {
      cursor: default;
    }
  }
}

.v-text-field {
  &.mt-0 {
    margin-top: 0 !important;
  }

  &.inline-text-field {
    max-width: 50px;
    margin: 0 0.5rem;
    display: inline-block;
  }

  &.text-center {
    input {
      text-align: center;
    }
  }

  &.no-spinner {
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
      -webkit-appearance: none;
    }

    input[type='number'] {
      -moz-appearance: textfield;
    }
  }
}

.v-btn {
  &.primary {
    color: $black !important;
    background: $primary !important;
  }

  &.link {
    font-weight: bold;
    color: $secondary !important;
    padding: 0 !important;
    font-size: 0.8rem !important;
    text-transform: capitalize;

    &:before {
      display: none;
    }
  }
}

.v-btn-toggle--group.status-button-group {
  > .v-btn.v-btn {
    border-radius: 50px !important;
    height: 30px !important;
    font-size: 12px;
    border-color: rgba(0, 0, 0, 0.4) !important;
    border-left-width: 1px !important;
    transition:
      background-color 0.2s ease,
      color 0.2s ease,
      border 0.2s ease;

    &.scheduled {
      &:hover {
        background-color: $scheduled !important;
        border-color: $scheduled !important;
        color: $white;
      }
    }

    &.arrived {
      &:hover {
        background-color: $arrived !important;
        border-color: $arrived !important;
        color: $white;
      }
    }

    &.noshow {
      &:hover {
        background-color: $noshow !important;
        border-color: $noshow !important;
        color: $white;
      }
    }

    &.cancelled {
      &:hover {
        background-color: $cancelled !important;
        border-color: $cancelled !important;
        color: $white;
      }
    }

    &.completed {
      &:hover {
        background-color: $completed !important;
        border-color: $completed !important;
        color: $white;
      }
    }

    &.v-item {
      &--active {
        background-color: $secondary !important;
        color: $white;

        &.scheduled {
          background-color: $scheduled !important;
          border-color: $scheduled !important;
        }

        &.arrived {
          background-color: $arrived !important;
          border-color: $arrived !important;
        }

        &.noshow {
          background-color: $noshow !important;
          border: $noshow !important;
        }

        &.cancelled {
          background-color: $cancelled !important;
          border: $cancelled !important;
        }

        &.completed {
          background-color: $completed !important;
          border: $completed !important;
        }
      }
    }
  }
}

.secondary-button-group {
  button {
    &.v-item--active {
      background-color: $secondary !important;
      color: $white !important;
      border: none !important;
      border: 1px solid $secondary !important;

      i {
        color: $white !important;
      }
    }
  }
}

.primary-button-group {
  button {
    &.v-item--active {
      background-color: $primary !important;
      color: $black !important;
      border: none !important;
      border: 1px solid $primary !important;

      i {
        color: $black !important;
      }
    }
  }
}

.v-card__actions {
  &.card-actions {
    justify-content: center;
    border-top: 1px solid rgba(0, 0, 0, 0.2);
    padding: 1rem;
    position: sticky;
    bottom: 0;
    z-index: 1;
  }
}

.v-btn-toggle {
  &[vertical] {
    flex-direction: column;

    .v-btn {
      border-radius: 5px !important;
    }

    > .v-btn:not(:first-child) {
      border-left-width: 1px !important;
    }
  }
}
</style>
